import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import dateFormat from 'dateformat';
import { times, sortBy, compact } from 'lodash';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import Button from 'reactstrap/lib/Button';
import { useToggle } from 'site-modules/shared/hooks/use-toggle';
import { getValidImageUrl } from 'client/utils/image-helpers';
import { CmsEntities } from 'client/data/models/cms';
import { EdmundsLogo } from 'site-modules/shared/components/edmunds-logo/edmunds-logo';
import { SocialShareDialog } from 'site-modules/shared/components/social-share-dialog/social-share-dialog';
import { Link } from 'site-modules/shared/components/link/link';

import './leaderboard-infographic.scss';

const LEGEND_BAR_CLASS_NAME = ['estimate-graph', 'tested-graph'];

function getPercentage(partialValue, totalValue) {
  return parseFloat((100 * partialValue) / totalValue).toFixed(2);
}

export function LeaderboardInfographic({
  className,
  titleClassName,
  titleColorClassName,
  legendClassName,
  creativeId,
  trackingId,
  content,
  widgetName,
  pathname,
  title,
  customLogo,
  bottomLogo,
  barsMap,
  stepValue,
  maxValue,
  chartValueMeasure,
  isWidget,
  previewMode,
}) {
  const [isModalOpen, toggleIsModalOpen] = useToggle(false);

  const updated =
    content.updated ||
    content
      .child('vehicles')
      .metadata('updated')
      .value();
  const vehiclesContent = content.child('vehicles').children();
  const vehicles = vehiclesContent
    .slice(0, previewMode || vehiclesContent.length)
    .map((entry, index) => ({ id: `${entry.id}-${index}`, ...entry.getAllMetadata() }));
  const biggestValue = vehicles.reduce(
    (result, vehicle) => Math.max(result, ...compact(barsMap).map(({ key }) => vehicle[key])),
    0
  );
  // E.g. From 365 to 400 (stepValue === 100)
  const biggestCeiledValue = Math.max(maxValue, Math.ceil(biggestValue / stepValue) * stepValue);
  // E.g. From 400 to [0, 100, 200, 300, 400] (stepValue === 100)
  const valueSections = times(biggestCeiledValue / stepValue + 1, num => num * stepValue);

  return (
    <Fragment>
      {!previewMode && (
        <Button color="outline-primary-b" onClick={toggleIsModalOpen} className="text-uppercase mb-2">
          Embed leaderboard
        </Button>
      )}
      <div
        className={classnames('leaderboard-infographic pt-2 px-1 px-md-2 pb-0_5', className)}
        data-tracking-parent={creativeId}
      >
        <div className="d-flex justify-content-center align-items-baseline mb-0_5">
          {customLogo || <EdmundsLogo path={null} />}
        </div>
        {!!title && (
          <div className="title-wrapper mx-auto">
            <p className={classnames('title', titleClassName, titleColorClassName)}>{title}</p>
          </div>
        )}
        <div className="d-flex justify-content-center mb-3">
          {barsMap.map(
            (bar, index) =>
              !!bar && (
                <div key={bar.key} className="d-flex align-items-center px-1">
                  <div className={classnames('legend me-0_25', LEGEND_BAR_CLASS_NAME[index])} />
                  <span className={legendClassName}>{bar.label}</span>
                </div>
              )
          )}
        </div>
        <div className="ratings pe-0_5">
          <Row className="sections-overlay w-100 justify-content-end pe-0_5" noGutters aria-hidden>
            <Col xs={6} md={7} className="d-flex justify-content-between">
              {valueSections.map(num => (
                <div key={num} className="section-label pos-r size-14 text-gray-darker">
                  <div className="label-text pos-a top-0 center-x">{num}</div>
                </div>
              ))}
            </Col>
          </Row>
          {sortBy(vehicles, vehicle => +vehicle.ratingPosition).map(
            ({ id, ratingPosition, imageURL, vehicleName, href, ...vehicleData }) => {
              const vehicleNameProps =
                !previewMode && href
                  ? { tag: Link, to: href, 'data-tracking-id': trackingId, target: isWidget ? '_blank' : undefined }
                  : { tag: 'p' };

              return (
                <Row key={id} className="leaderboard-row align-items-center mb-2" noGutters>
                  <Col xs={2} className="position-section">
                    <span className="position-label text-primary d-flex align-items-start">
                      <span className="sub-label size-16">#</span>
                      <span className="number-label fw-bold">{ratingPosition}</span>
                    </span>{' '}
                    <img src={getValidImageUrl(imageURL)} alt="" className="mw-100" style={{ maxHeight: '53px' }} />
                  </Col>
                  <Col
                    {...vehicleNameProps}
                    className="vehicle-name mb-0 size-16 text-primary-darker px-0_25"
                    xs={4}
                    md={3}
                  >
                    {vehicleName}
                  </Col>
                  <Col xs={6} md={7} className="chart-section">
                    {!!barsMap[0] && (
                      <div
                        className="estimate d-flex flex-column mb-0_25"
                        style={{ width: `${getPercentage(vehicleData[barsMap[0].key], biggestCeiledValue)}%` }}
                      >
                        <span className="estimated-label text-nowrap size-12 text-cool-gray-30 mb-0_25 text-end">
                          {vehicleData[barsMap[0].key]} {chartValueMeasure}
                        </span>{' '}
                        <div className="estimate-graph" />
                      </div>
                    )}

                    <div className="tested d-flex align-items-center">
                      <div
                        className="tested-graph pos-r"
                        style={{ width: `${getPercentage(vehicleData[barsMap[1].key], biggestCeiledValue)}%` }}
                      />
                      <span className="tested-label text-nowrap medium text-primary-darker ms-0_25">
                        {vehicleData[barsMap[1].key]} {chartValueMeasure}
                      </span>
                    </div>
                  </Col>
                </Row>
              );
            }
          )}
        </div>
        <div className="d-flex justify-content-between align-items-center pb-0_5">
          {bottomLogo}
          {!!updated && (
            <p className="date-label text-primary-darker text-uppercase fw-bold small mb-0 ms-auto">
              Updated {dateFormat(updated, 'mmmm d, yyyy')}
            </p>
          )}
        </div>
      </div>
      {!previewMode && (
        <SocialShareDialog
          className="embeddable-infographic-modal"
          isOpen={isModalOpen}
          toggle={toggleIsModalOpen}
          headingText="Embed Code"
          pageUrl={`https://www.edmunds.com${pathname}`}
          shareableWidgetData={{
            widgetName,
            defaultWidth: 640,
            defaultHeight: 1600,
            minWidth: 290,
            minHeight: 800,
            defaultTitle: title,
          }}
          hideSocialIcons
        />
      )}
    </Fragment>
  );
}

LeaderboardInfographic.propTypes = {
  title: PropTypes.string,
  widgetName: PropTypes.string.isRequired,
  content: CmsEntities.Content.isRequired,
  // Must always have 2 values [estimated, tested]. Can be [null, tested]
  barsMap: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      key: PropTypes.string,
      labelInsideThreshold: PropTypes.number,
    })
  ).isRequired,
  stepValue: PropTypes.number.isRequired,
  customLogo: PropTypes.node,
  bottomLogo: PropTypes.node,
  maxValue: PropTypes.number,
  className: PropTypes.string,
  titleClassName: PropTypes.string,
  titleColorClassName: PropTypes.string,
  legendClassName: PropTypes.string,
  chartValueMeasure: PropTypes.string,
  pathname: PropTypes.string,
  creativeId: PropTypes.string,
  trackingId: PropTypes.string,
  isWidget: PropTypes.bool,
  previewMode: PropTypes.number,
};

LeaderboardInfographic.defaultProps = {
  title: null,
  customLogo: null,
  bottomLogo: null,
  maxValue: null,
  className: null,
  titleClassName: 'text-center heading-1 mb-2 text-uppercase fw-bold',
  titleColorClassName: 'text-blue-50',
  legendClassName: 'size-16 text-gray-darker',
  chartValueMeasure: null,
  pathname: null,
  creativeId: null,
  trackingId: null,
  isWidget: false,
  hideEmbedBtn: false,
  previewMode: 0,
};
